import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { fieldClass, labelClass, primaryBtnClass } from '../styles';
import { fetchPlatformSettings, updatePlatformSetting } from './api';
import type { PlatformSettingRow } from './types';

const PlatformManagementPanel: React.FC = () => {
  const [rows, setRows] = useState<PlatformSettingRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPlatformSettings();
      setRows(data);
      const next: Record<number, string> = {};
      data.forEach((row) => {
        next[row.id] = row.value;
      });
      setDrafts(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const dirtyIds = useMemo(() => {
    return rows
      .filter((row) => (drafts[row.id] ?? '') !== row.value)
      .map((row) => row.id);
  }, [rows, drafts]);

  const save = async () => {
    if (dirtyIds.length === 0) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await Promise.all(
        dirtyIds.map((id) => updatePlatformSetting(id, (drafts[id] ?? '').trim()))
      );
      setRows((prev) =>
        prev.map((row) => updated.find((u) => u.id === row.id) ?? row)
      );
      setDrafts((prev) => {
        const next = { ...prev };
        updated.forEach((row) => {
          next[row.id] = row.value;
        });
        return next;
      });
      setMessage(`Saved ${updated.length} setting${updated.length === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] py-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {(error || message) && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || message}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px] text-[#626262]">
          Edit platform setting values only. Keys and descriptions are read-only.
        </p>
        <button
          type="button"
          disabled={saving || dirtyIds.length === 0}
          onClick={() => void save()}
          className={`${primaryBtnClass} inline-flex items-center gap-1.5`}
        >
          <Save className="w-3.5 h-3.5" />
          Save{dirtyIds.length ? ` (${dirtyIds.length})` : ''}
        </button>
      </div>
      <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-[#FAFAFA] text-[12px] text-[#626262]">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Key</th>
              <th className="text-left px-3 py-2 font-medium">Value</th>
              <th className="text-left px-3 py-2 font-medium">Description</th>
              <th className="text-left px-3 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const dirty = (drafts[row.id] ?? '') !== row.value;
              return (
                <tr
                  key={row.id}
                  className={`border-t border-[#F0F0F0] ${dirty ? 'bg-[#FFFBEB]' : ''}`}
                >
                  <td className="px-3 py-2 font-mono text-[12px] text-[#0C0B0B] whitespace-nowrap">
                    {row.key}
                  </td>
                  <td className="px-3 py-2 min-w-[160px]">
                    <label className="sr-only">{labelClass} Value for {row.key}</label>
                    <input
                      className={fieldClass}
                      value={drafts[row.id] ?? ''}
                      onChange={(e) =>
                        setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))
                      }
                    />
                  </td>
                  <td className="px-3 py-2 text-[#4A4A4A] max-w-[360px]">
                    {row.description || '—'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[#8A8A8A]">
                    {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-[#8A8A8A]">
                  No platform settings
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlatformManagementPanel;
